import { FC } from 'react';
import { Box, Grid, Stack, Typography } from '@mui/material';
import useFilters from '../../app/hooks/useFilters';
import { useGetFactorReportQuery } from '../../app/api';
import { UILoader } from '../../shared/ui/ui-loader';
import { UIGridItem } from '../../shared/ui/ui-grid-item';
import { DatePicker } from '@mui/x-date-pickers';
import UIAutocomplete from '../../shared/ui/ui-autocmplete';
import { EmptyDataPlaceholder } from '../../shared/ui/empty-data-placeholder';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';
import { Currency, GetClientsList, GetOKEDList } from '../../app/api/types';
import { numericFormatter } from 'react-number-format';
import { CURRENCY_OPTIONS } from '@/constants';
import MoneyIcon from '@/app/assets/icons/money.svg?react';
import PercentIcon from '@/app/assets/icons/percent.svg?react';
import HandshakeIcon from '@/app/assets/icons/handshake.svg?react';
import { getCurrency } from '@/app/helpers';
import { FactorReportTable } from './ui/factor-report-table';
import { useNavigate } from 'react-router-dom';

interface Props {
  clientsList: GetClientsList[] | undefined;
  okedList: GetOKEDList[] | undefined;
}

interface Filters {
  dateFrom: string;
  dateTo: string;
  OKEDCode: string;
  clientTin: string;
  currency: Currency;
}

const defaultFilters: Filters = {
  dateFrom: '',
  dateTo: dayjs().format('DD.MM.YYYY'),
  OKEDCode: '',
  clientTin: '',
  currency: 'UZS',
};

const formatAmount = (value: string | number | undefined) =>
  numericFormatter(String(value), { thousandSeparator: ' ' });

export const FactorBriefCase: FC<Props> = ({ clientsList, okedList }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { filters, updateFilters, nonEmptyFilters } = useFilters<Filters>({
    defaultFilters,
  });

  const { data, isLoading } = useGetFactorReportQuery(nonEmptyFilters);

  if (isLoading) {
    return <UILoader />;
  }

  const handleGoToFactorDeals = () => {
    const params = new URLSearchParams({
      activeTab: '1',
      dateFrom: filters.dateFrom || '',
      dateTo: filters.dateTo,
      page: '1',
      pageSize: '10',
      currency: filters.currency,
      status: 'overdue',
      search: filters.clientTin,
    });
    navigate(`/factor/main?${params.toString()}`);
  };

  return (
    <Stack spacing={2.5}>
      <Stack direction='row' justifyContent='space-between'>
        <Stack direction='row' spacing={3}>
          <Stack direction='row' spacing={1}>
            <DatePicker
              label={t('from') + ':'}
              format='DD.MM.YYYY'
              value={
                dayjs(filters.dateFrom, 'DD.MM.YYYY').isValid()
                  ? dayjs(filters.dateFrom, 'DD.MM.YYYY')
                  : null
              }
              onChange={(value) =>
                updateFilters({
                  dateFrom: dayjs(value).isValid()
                    ? dayjs(value).format('DD.MM.YYYY')
                    : '',
                })
              }
            />
            <DatePicker
              label={t('to') + ':'}
              format='DD.MM.YYYY'
              value={
                dayjs(filters.dateTo, 'DD.MM.YYYY').isValid()
                  ? dayjs(filters.dateTo, 'DD.MM.YYYY')
                  : null
              }
              onChange={(value) =>
                updateFilters({
                  dateTo: dayjs(value).isValid()
                    ? dayjs(value).format('DD.MM.YYYY')
                    : '',
                })
              }
            />
          </Stack>

          <UIAutocomplete
            value={
              clientsList?.find((item) => item.tin === filters.clientTin) ??
              null
            }
            options={clientsList ?? []}
            TextFieldProps={{
              label: t('client_name'),
              sx: { width: 300 },
            }}
            getOptionLabel={(option) => option.shortName}
            onChange={(_event, value) => {
              updateFilters({ clientTin: value?.tin });
            }}
          />

          <UIAutocomplete
            value={CURRENCY_OPTIONS.find(
              (item) => item.value === filters.currency
            )}
            options={CURRENCY_OPTIONS}
            TextFieldProps={{
              label: t('currency'),
              sx: { width: 125 },
            }}
            getOptionLabel={(option) => {
              return t(option.label);
            }}
            onChange={(_event, value) => {
              updateFilters({ currency: value?.value as Currency });
            }}
            disableClearable
          />
        </Stack>
      </Stack>

      <EmptyDataPlaceholder isEmpty={!data?.data} title={t('no_data')}>
        <Stack spacing={1.5}>
          <Box>
            <Grid container spacing={2}>
              <UIGridItem
                title={
                  t('all_deals_sum') +
                  `, ${t(getCurrency(filters.currency)).toLowerCase()}:`
                }
                subtitle={formatAmount(data?.data.dealsTotalAmount)}
                gridItemProps={{ xs: 12, md: 5 }}
                icon={<MoneyIcon />}
                gradient
              />
              <UIGridItem
                title={
                  t('percent') +
                  `, ${t(getCurrency(filters.currency)).toLowerCase()}:`
                }
                subtitle={formatAmount(data?.data.comissionAmount)}
                gridItemProps={{ xs: 12, md: 3.5 }}
                icon={<PercentIcon />}
                expandedRows={[
                  {
                    label: t('comission'),
                    value: formatAmount(data?.data.commissionOnlyAmount),
                  },
                  {
                    label: t('discount_2'),
                    value: formatAmount(data?.data.discountAmount),
                  },
                ]}
              />
              <UIGridItem
                title={t('deals_count') + ':'}
                subtitle={formatAmount(data?.data.numberOfDeals)}
                gridItemProps={{ xs: 12, md: 3.5 }}
                icon={<HandshakeIcon />}
              />
              <UIGridItem
                title={t('clients_count') + ':'}
                subtitle={formatAmount(data?.data.numberOfClients)}
                gridItemProps={{ xs: 12, md: 3.5 }}
              />
              <UIGridItem
                title={t('expired_deals') + ':'}
                subtitle={formatAmount(data?.data.expiredDealsNumber)}
                gridItemProps={{ xs: 12, md: 3.5 }}
                onClick={handleGoToFactorDeals}
              />
              <UIGridItem
                title={
                  t('expired_deals_payment_sum') +
                  `, ${t(getCurrency(filters.currency)).toLowerCase()}:`
                }
                subtitle={formatAmount(data?.data.expiredDealsAmount)}
                gridItemProps={{ xs: 12, md: 5 }}
                onClick={handleGoToFactorDeals}
              />
            </Grid>
          </Box>
        </Stack>

        <Stack direction='row' alignItems='center' gap={2} mb={2}>
          <Typography variant='h5'>{t('clients_by_oked')}:</Typography>

          <UIAutocomplete
            value={
              okedList?.find((item) => item.code === filters.OKEDCode) ?? null
            }
            options={okedList ?? []}
            TextFieldProps={{
              label: t('filter'),
              sx: { width: 240 },
            }}
            getOptionLabel={(option) => option.name}
            onChange={(_event, value) => {
              updateFilters({ OKEDCode: value?.code });
            }}
          />
        </Stack>
        <FactorReportTable data={data?.data.clientsByOKED} />
      </EmptyDataPlaceholder>
    </Stack>
  );
};
